import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, ShieldCheck, ShieldAlert, Loader2, Archive } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getDraw, verifyDraw, actaPdfUrl, bundleZipUrl } from "@/lib/api";
import { TIPO_LABEL, isVisibleTipo, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { GameResult } from "@/components/GameResult";
import { HashBlock } from "@/components/CopyButton";

export default function DrawDetail() {
  const { id } = useParams();
  const [draw, setDraw] = useState(null);
  const [verif, setVerif] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getDraw(id).then(setDraw).catch(() => setDraw(false));
  }, [id]);

  const onVerify = async () => {
    setLoading(true);
    try {
      const v = await verifyDraw(id);
      setVerif(v);
    } finally {
      setLoading(false);
    }
  };

  if (draw === false) return <div className="mx-auto max-w-6xl px-4 py-16">Sorteo no encontrado.</div>;
  if (!draw) return <div className="mx-auto max-w-6xl px-4 py-16">Cargando…</div>;
  if (!isVisibleTipo(draw.tipo)) return <div className="mx-auto max-w-6xl px-4 py-16" data-testid="draw-not-available">Sorteo no disponible.</div>;

  const revealed = draw.estado === "revelado";

  return (
    <div data-theme={draw.tipo}>
      <section className="relative overflow-hidden border-b border-border" style={{ background: "hsl(var(--game-surface))" }}>
        <div className="noise-overlay" />
        <div className="relative mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-10">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "hsl(var(--game-accent))" }}>{TIPO_LABEL[draw.tipo]}</span>
            <StatusBadge estado={draw.estado} testId="draw-detail-estado" />
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold sm:text-3xl">{draw.nombre}</h1>
          <div className="mt-6"><GameResult tipo={draw.tipo} resultado={draw.resultado} /></div>
          <p className="mt-4 text-sm text-muted-foreground">Ejecutado: {formatDate(draw.executed_at)}</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-10" data-theme="institutional">
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="font-display text-lg">Evidencia Provably-Fair</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Field label="Compromiso (SHA-256) publicado antes del sorteo"><HashBlock value={draw.commitment} testId="draw-detail-commitment" /></Field>
              <Field label="Semilla del cliente (client seed)"><HashBlock value={draw.client_seed} /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Nonce"><div className="rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-sm">{draw.nonce}</div></Field>
                <Field label="Índice en la bitácora"><div className="rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-sm">{draw.ledger_index ?? "—"}</div></Field>
              </div>
              <Field label={`Semilla del servidor ${revealed ? "(revelada)" : "(se revela tras el sorteo)"}`}>
                <HashBlock value={revealed ? draw.server_seed : "Aún no revelada"} testId="draw-detail-server-seed" />
              </Field>
              <Field label="Hash del registro (record_hash)"><HashBlock value={draw.record_hash} /></Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-display text-lg">Verificación y descargas</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Recomputamos el resultado y validamos el compromiso de forma independiente.</p>
              <Button className="w-full gap-2" onClick={onVerify} disabled={!revealed || loading} data-testid="draw-detail-verify-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Verificar integridad
              </Button>
              {!revealed && <p className="text-xs text-muted-foreground">La verificación estará disponible cuando el sorteo sea revelado.</p>}
              {verif && (
                <Alert className={verif.pass ? "border-success/40 bg-emerald-50" : "border-destructive/40 bg-red-50"} data-testid="draw-detail-verify-result">
                  {verif.pass ? <ShieldCheck className="h-4 w-4 text-success" /> : <ShieldAlert className="h-4 w-4 text-destructive" />}
                  <AlertTitle>{verif.pass ? "Verificado ✓" : "Verificación fallida"}</AlertTitle>
                  <AlertDescription className="text-xs">
                    Compromiso: {verif.commitment_ok ? "OK" : "FALLA"} · Resultado: {verif.resultado_ok ? "OK" : "FALLA"}
                  </AlertDescription>
                </Alert>
              )}
              <a href={actaPdfUrl(draw.id)} target="_blank" rel="noreferrer" data-testid="draw-detail-download-acta-button">
                <Button variant="outline" className="w-full gap-2"><Download className="h-4 w-4" /> Descargar acta (PDF)</Button>
              </a>
              <a href={bundleZipUrl(draw.id)} target="_blank" rel="noreferrer" data-testid="draw-detail-download-bundle-button">
                <Button variant="outline" className="w-full gap-2"><Archive className="h-4 w-4" /> Paquete de auditoría (ZIP)</Button>
              </a>
              <Link to="/verificar" className="block text-center text-xs text-primary hover:underline">Verificar manualmente →</Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <p className="mb-1.5 text-xs font-medium text-muted-foreground">{label}</p>
    {children}
  </div>
);
